import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Loading,
  Modal,
  PageHeader,
  Stat,
  TextInput,
} from "../../components/ui";
import { api, errorMessage, qs } from "../../lib/api";
import { brl, dateBR, dateTimeBR, monthLabel } from "../../lib/format";
import type { PayoutStatus } from "../../lib/types";
import { PAYOUT_STATUS } from "../sales/SellerFinance";

interface PayoutsData {
  period: string;
  scheduledFor: string;
  completedAt: string | null;
  forecast: {
    period: string;
    scheduledFor: string;
    totalCents: number;
    teachersCents: number;
    sellersCents: number;
    people: number;
    balanceCents: number | null;
  };
  items: {
    id: string;
    ownerType: "teacher" | "seller";
    ownerName: string;
    walletId: string;
    creditsCents: number;
    debitsCents: number;
    amountCents: number;
    status: PayoutStatus;
    asaasTransferId: string | null;
    asaasStatus: string | null;
    error: string | null;
    attempts: number;
    sentAt: string | null;
  }[];
}

const ADMIN_STATUS: Record<PayoutStatus, string> = {
  sent: "Transferido",
  processing: "Enviando",
  failed: "Recusado",
  unknown: "Sem resposta",
};

/** Repasses mensais ao professor e à vendedora (ADR 0006). */
export function AdminPayouts() {
  const qc = useQueryClient();
  const [period, setPeriod] = useState("");
  const q = useQuery({
    queryKey: ["payouts", period],
    queryFn: () => api.get<PayoutsData>(`/v1/admin/payouts${qs({ period: period || undefined })}`),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["payouts"] });
  const run = useMutation({
    mutationFn: () => api.post<{ status: string }>("/v1/admin/payouts/run"),
    onSuccess: refresh,
  });
  const retry = useMutation({
    mutationFn: (id: string) => api.post(`/v1/admin/payouts/${id}/retry`),
    onSuccess: refresh,
  });
  const [marking, setMarking] = useState<string | null>(null);
  const [transferId, setTransferId] = useState("");
  const mark = useMutation({
    mutationFn: () =>
      api.post(`/v1/admin/payouts/${marking}/mark-sent`, {
        ...(transferId ? { transferId } : {}),
      }),
    onSuccess: () => {
      setMarking(null);
      setTransferId("");
      refresh();
    },
  });
  const f = q.data?.forecast;
  const short = f && f.balanceCents !== null && f.balanceCents < f.totalCents;
  return (
    <>
      <PageHeader
        title="Repasses"
        subtitle="O que foi pago num mês é transferido para a subconta do professor e da vendedora no dia útil configurado do mês seguinte."
        actions={
          <Button variant="secondary" loading={run.isPending} onClick={() => run.mutate()}>
            Processar agora
          </Button>
        }
      />
      {run.data ? (
        <Alert kind={run.data.status === "insufficient_balance" ? "warning" : "note"}>
          {run.data.status === "waiting"
            ? "Ainda não chegou a data do repasse."
            : run.data.status === "insufficient_balance"
              ? "Saldo insuficiente na Asaas: antecipe os recebíveis do cartão ou deposite e processe de novo."
              : "Repasse processado."}
        </Alert>
      ) : null}
      {run.error ? <Alert kind="risk">{errorMessage(run.error)}</Alert> : null}
      {q.isLoading ? <Loading /> : null}
      {q.error ? <Alert kind="risk">{errorMessage(q.error)}</Alert> : null}
      {f ? (
        <div className="grid cols-4 keep-2" style={{ marginBottom: 16 }}>
          <Stat
            label={`Próximo repasse, em ${dateBR(f.scheduledFor)}`}
            value={brl(f.totalCents)}
            hint={`${f.people} pessoas, pagamentos de ${monthLabel(f.period)}`}
          />
          <Stat label="Professores" value={brl(f.teachersCents)} />
          <Stat label="Vendedoras" value={brl(f.sellersCents)} />
          <Stat
            label="Saldo na Asaas"
            value={f.balanceCents === null ? "Indisponível" : brl(f.balanceCents)}
            hint={short ? `Faltam ${brl(f.totalCents - (f.balanceCents ?? 0))}` : undefined}
          />
        </div>
      ) : null}
      {short ? (
        <Alert kind="warning">
          O saldo não cobre o próximo repasse. Antecipe os recebíveis do cartão na Asaas ou deposite
          a diferença antes da data.
        </Alert>
      ) : null}
      <div className="row" style={{ margin: "16px 0" }}>
        <TextInput
          label="Competência"
          type="month"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
        />
      </div>
      {q.data ? (
        <Card
          title={`Pagamentos de ${monthLabel(q.data.period)}, repasse em ${dateBR(q.data.scheduledFor)}`}
        >
          {q.data.items.length === 0 ? <p className="muted">Nenhum repasse feito.</p> : null}
          {q.data.items.length > 0 ? (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Pessoa</th>
                    <th className="num">Pagamentos</th>
                    <th className="num">Estornos</th>
                    <th className="num">Repasse</th>
                    <th>Situação</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {q.data.items.map((i) => (
                    <tr key={i.id}>
                      <td>
                        {i.ownerName}
                        <div className="small muted">
                          {i.ownerType === "teacher" ? "Professor" : "Vendedora"}
                        </div>
                      </td>
                      <td className="num">{brl(i.creditsCents)}</td>
                      <td className="num">{i.debitsCents ? brl(i.debitsCents) : ""}</td>
                      <td className="num">
                        <strong>{brl(i.amountCents)}</strong>
                      </td>
                      <td>
                        <Badge color={PAYOUT_STATUS[i.status].color}>
                          {ADMIN_STATUS[i.status]}
                        </Badge>
                        <div className="small muted">
                          {i.sentAt ? dateTimeBR(i.sentAt) : null}
                          {i.error ? ` ${i.error}` : null}
                        </div>
                      </td>
                      <td>
                        {i.status === "failed" || i.status === "unknown" ? (
                          <div className="row">
                            <Button
                              size="small"
                              variant="secondary"
                              loading={retry.isPending && retry.variables === i.id}
                              onClick={() => {
                                if (
                                  i.status === "unknown" &&
                                  !window.confirm(
                                    "Confira no painel da Asaas se a transferência não saiu. Tentar de novo?",
                                  )
                                )
                                  return;
                                retry.mutate(i.id);
                              }}
                            >
                              Tentar de novo
                            </Button>
                            {i.status === "unknown" ? (
                              <Button size="small" variant="ghost" onClick={() => setMarking(i.id)}>
                                Marcar como transferido
                              </Button>
                            ) : null}
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {retry.error ? <Alert kind="risk">{errorMessage(retry.error)}</Alert> : null}
        </Card>
      ) : null}
      {marking ? (
        <Modal title="Transferência feita" onClose={() => setMarking(null)}>
          <div className="stack">
            <p className="small muted">
              Use quando a transferência aparece no painel da Asaas, mas a plataforma não recebeu a
              confirmação.
            </p>
            <TextInput
              label="ID da transferência na Asaas (opcional)"
              value={transferId}
              onChange={(e) => setTransferId(e.target.value)}
            />
            {mark.error ? <Alert kind="risk">{errorMessage(mark.error)}</Alert> : null}
            <Button loading={mark.isPending} onClick={() => mark.mutate()}>
              Marcar como transferido
            </Button>
          </div>
        </Modal>
      ) : null}
    </>
  );
}
